import { createContext, useContext, useState, useRef } from "preact/compat";
import { productSlugFormat, formatStrainType, formatter, calculatePercentOffPrice } from "@/Utils";
import AddToWishlistButton from "@/components/AddToWishlistButton";
import AddToCartButton from "@/components/AddToCartButton";
import WeightSelectButton from "@/components/WeightSelectButton";
import CloseIcon from "@/icons/CloseIcon";
import { useClickOutside } from "@/hooks/useClickOutside";
export const ProductContext = createContext();

function ProductCard({ product, className, retailer, children }) {
	const [slideoutOpen, setSlideoutOpen] = useState(false);
	let slug = product.slug;
	if (retailer) {
		slug = productSlugFormat(product, retailer);
	}

	function toggleWeightSlideout() {
		setSlideoutOpen(!slideoutOpen);
	}

	return (
		<ProductContext.Provider value={{ product, slug, toggleWeightSlideout, slideoutOpen, setSlideoutOpen, retailer }}>
			<div className={`product-card ${className || ''}`}>
				{children}
			</div>
		</ProductContext.Provider>
	);
}

function Header({ children }) {
	return <div className="product-card-header">{children}</div>;
}

function Image({ children, loadingStyle="lazy" }) {
	const product = useContext(ProductContext).product;
	const slug = useContext(ProductContext).slug;

	return (
		<a href={`/product/${slug}/`} className="product-card-image">
			<img src={`${product.images[0].url}?w=668&h=668`} height="334" width="334" alt={product.name} loading={loadingStyle} />
			<DetailsOverlay />
		</a>
	);
}

function DetailsOverlay() {
	const product = useContext(ProductContext).product;
	let specialPrice, percentageDiscount;
	let price = formatter(product.variants[0].priceRec);
	if ( product.variants[0].specialPriceRec ) {
		specialPrice = formatter(product.variants[0].specialPriceRec);
		percentageDiscount = calculatePercentOffPrice(product.variants[0].priceRec, product.variants[0].specialPriceRec);
	}

	return (
		<div className="product-details-overlay">
			<AddToWishlistButton product={product} />
			{product.staffPick ? (
				<span className="product-card-staff-pick pill">Staff Pick</span>
			) : percentageDiscount ? (
				<span className="product-card-discount pill">{percentageDiscount}% off</span>
			) : null}
		</div>
	);
}
  
function Content({ children }) {
	return <div className="product-card-content">{children}</div>;
}

function Title() {
	const slug = useContext(ProductContext).slug;
	const product = useContext(ProductContext).product;
	return (
		<h3 className="product-card-title">
			<a href={`/product/${slug}/`}>{product.name}</a>
		</h3>
	)
}

function MobileDiscount() {
	const product = useContext(ProductContext).product;
	let specialPrice, percentageDiscount;
	let price = formatter(product.variants[0].priceRec);
	if ( product.variants[0].specialPriceRec ) {
		specialPrice = formatter(product.variants[0].specialPriceRec);
		percentageDiscount = calculatePercentOffPrice(product.variants[0].priceRec, product.variants[0].specialPriceRec);
	}

	return (
		<>
			{ percentageDiscount ? (
				<span className="product-card-discount pill mobile-discount">{percentageDiscount}% off</span>
			) : null}
		</>
	);
}

function Pricing({ style="mobile" }) {
	const product = useContext(ProductContext).product;
	let specialPrice;
	let price = formatter(product.variants[0].priceRec);
	let category = product.category;

	if ( product.variants[0].specialPriceRec ) {
		specialPrice = formatter(product.variants[0].specialPriceRec);
	}
	return (
		<div className={`product-card-pricing ${style}`}>
			<span className="product-price">
				{ specialPrice ? specialPrice : price }
			</span>
			
			{product?.variants[0].option !== "N/A" && category !== 'EDIBLES' ? (
				<span className="product-variant">| {product?.variants[0].option}</span>
			) : null}
			
			{ specialPrice ? (
				<strike>{price}</strike>
			) : null}
		</div>
	);
}

function Brand() {
	const retailerId = useContext(ProductContext).retailer?.id;
	const product = useContext(ProductContext).product;
	
	if ( ! product.brand ) {
		return null;
	}

	if ( ! dutchie?.brands[retailerId] ) {
		return null;
	}

	const matchedBrand = dutchie?.brands[retailerId].find((b) => b.id == product?.brand?.id);

	if ( ! matchedBrand ) {
		return (
			<span className="product-card-brand">{product?.brand?.name}</span>
		)
	}

	return (
		<a href={matchedBrand?.permalink} className="product-card-brand">{product?.brand?.name}</a>
	)
}

function Meta() {
	const product = useContext(ProductContext).product;
	return (
		<ul className="product-card-meta">
			{product.potencyThc?.formatted.length !== 0 && (
				<li className="product-card-thc">
					THC: {product.potencyThc?.formatted}
				</li>
			)}
			{product.potencyCbd?.formatted.length !== 0 && (
				<li className="product-card-cbd">
					CBD: {product.potencyCbd?.formatted}
				</li>
			)}

			{product.strainType !== 'NOT_APPLICABLE' ? (
				<li className="product-card-strain-type">{ formatStrainType(product.strainType) }</li>
			) : null}
		</ul>
	);
}

function AddToCart({ children, classes='btn-primary-outline' }) {
	const product = useContext(ProductContext).product;
	let button;

	if (product.variants.length > 1) {
		button = <WeightSelectButton product={product} />
	} else {
		button = 
		<AddToCartButton product={product} quantity={1} classes={classes}>
			<AddToCartButton.Label>
				{children}
				<span className="cart-btn-label">Add To Cart</span>
			</AddToCartButton.Label>
		</AddToCartButton>
	}

	return (
		<>{button}</>
	)
}

function Slideout() {
	const isOpen = useContext(ProductContext).slideoutOpen;
	const { toggleWeightSlideout } = useContext(ProductContext);
	const { setSlideoutOpen } = useContext(ProductContext);
	const { product } = useContext(ProductContext);
	const [activeWeight, setActiveWeight] = useState(product.variants[0]);
	const slideoutRef = useRef();

	function onClickOutside() {
		setSlideoutOpen(false);
	}

	useClickOutside(slideoutRef, onClickOutside);

	return (
		<div className={`product-card-slideout ${isOpen ? 'open' : ''}`} ref={slideoutRef}>
			<header className="product-card-slideout-header">
				<p>Select weight</p>
				<button aria-label="Show Weights" className="btn-reset" onClick={() => toggleWeightSlideout()}>
					<CloseIcon color="#fff" />
				</button>
			</header>
			<section className="product-card-slideout-content">
				{product.variants ? (
					<div className="product-card-price-options">
						{product.variants.map((variant) => (
							<button
								onClick={() => setActiveWeight(variant)}
								className={`btn-sm btn-white-outline ${activeWeight.id == variant.id ? "active" : ""}`}
								key={variant.id}
								aria-label="Select weight"
								>
								<span>{variant.option}</span> - 
								<span className="product-price-card">
									{variant.specialPriceRec ? (
										<>{formatter(variant.specialPriceRec)} <strike>{formatter(variant.priceRec)}</strike></>
									) : (
										<>{formatter(variant.priceRec)}</>
									)}
								</span>
							</button>
						))}
					</div>
				) : null}
				<AddToCartButton product={product} quantity={1} weight={activeWeight} classes="btn-white-outline">
					<AddToCartButton.Label>Add To Cart</AddToCartButton.Label>
				</AddToCartButton>
			</section>
		</div>
	)
}
  
ProductCard.AddToCart = AddToCart;
ProductCard.Brand = Brand;
ProductCard.Header = Header;
ProductCard.Image = Image;
ProductCard.Title = Title;
ProductCard.Content = Content;
ProductCard.Pricing = Pricing;
ProductCard.Meta = Meta;
ProductCard.Slideout = Slideout;
ProductCard.MobileDiscount = MobileDiscount;

export default ProductCard;